import type { ContentPatch } from '../../../hooks/useSitePreview';
import type { DataFileName } from '../../../hooks/useWebsiteDataFile';

/**
 * previewPatches — derive live DOM patches from edited JSON.
 *
 * Each entry maps an edited data file to the set of `data-edit` keys that the
 * template (PreviewBridge) understands. The portal sends these patches into the
 * preview iframe so changes appear instantly, before Publish.
 *
 * EXTENDING (for whoever adds more sections): add a deriver below and tag the
 * matching element in template-monster with `data-edit="<key>"` (text),
 * `data-edit-src="<key>"` (image) or `data-edit-href="<key>"` (link). Keep keys
 * dot-namespaced by section, e.g. `services.0.name`, `about.headline`.
 */

export interface BusinessContext {
  businessName?: string;
  city?: string;
  state?: string;
  phone?: string;
  /** Raw phone number (no formatting) — used for tel: hrefs */
  phoneRaw?: string;
  /** 2-letter region / state abbreviation */
  region?: string;
  address?: string;
  email?: string;
  rating?: string;
  reviewCount?: string;
  yearsInBusiness?: string;
  establishedYear?: string;
}

/**
 * Mirror the template copyEngine's {{variable}} substitution for the common
 * variables so previewed text matches the rendered site.
 *
 * Variable list mirrors apps/template-monster/lib/copyEngine.ts CopyVariables.
 */
export function substituteVars(text: string, ctx: BusinessContext): string {
  if (!text) return text;
  return text
    .replace(/\{\{\s*businessName\s*\}\}/g, ctx.businessName || '')
    .replace(/\{\{\s*city\s*\}\}/g, ctx.city || '')
    .replace(/\{\{\s*state\s*\}\}/g, ctx.state || '')
    .replace(/\{\{\s*region\s*\}\}/g, ctx.region || ctx.state || '')
    .replace(/\{\{\s*phone\s*\}\}/g, ctx.phone || '')
    .replace(/\{\{\s*phoneRaw\s*\}\}/g, ctx.phoneRaw || '')
    .replace(/\{\{\s*address\s*\}\}/g, ctx.address || '')
    .replace(/\{\{\s*email\s*\}\}/g, ctx.email || '')
    .replace(/\{\{\s*rating\s*\}\}/g, ctx.rating || '')
    .replace(/\{\{\s*reviewCount\s*\}\}/g, ctx.reviewCount || '')
    .replace(/\{\{\s*yearsInBusiness\s*\}\}/g, ctx.yearsInBusiness || '')
    .replace(/\{\{\s*establishedYear\s*\}\}/g, ctx.establishedYear || '');
}

type Deriver = (data: any, ctx: BusinessContext) => ContentPatch[];

const DERIVERS: Partial<Record<DataFileName, Deriver>> = {
  // ---------------------------------------------------------------------------
  // content.json — hero, about, cta sections
  // ---------------------------------------------------------------------------
  content: (data, ctx) => {
    const patches: ContentPatch[] = [];

    // Hero block
    const hero = data?.hero;
    if (hero) {
      if (typeof hero.headline === 'string')
        patches.push({ key: 'hero.headline', value: substituteVars(hero.headline, ctx) });
      if (typeof hero.headlineAccent === 'string')
        patches.push({ key: 'hero.headlineAccent', value: substituteVars(hero.headlineAccent, ctx) });
      if (typeof hero.description === 'string')
        patches.push({ key: 'hero.description', value: substituteVars(hero.description, ctx) });
      if (typeof hero.heroImage === 'string')
        patches.push({ key: 'hero.image', value: hero.heroImage, kind: 'src' });
    }

    // About section
    const about = data?.aboutSection;
    if (about) {
      const headline = about.title || about.headline;
      const body = about.subtitle || about.description || about.body;
      if (typeof headline === 'string')
        patches.push({ key: 'about.headline', value: substituteVars(headline, ctx) });
      if (typeof body === 'string')
        patches.push({ key: 'about.body', value: substituteVars(body, ctx) });
    }

    // CTA Banner
    const cta = data?.ctaBanner;
    if (cta) {
      const ctaHeadline = cta.headline || cta.title;
      if (typeof ctaHeadline === 'string')
        patches.push({ key: 'cta.headline', value: substituteVars(ctaHeadline, ctx) });
      if (typeof cta.description === 'string')
        patches.push({ key: 'cta.description', value: substituteVars(cta.description, ctx) });
      if (typeof cta.buttonText === 'string')
        patches.push({ key: 'cta.buttonText', value: substituteVars(cta.buttonText, ctx) });
      if (typeof cta.buttonHref === 'string' && cta.buttonHref)
        patches.push({ key: 'cta.buttonHref', value: cta.buttonHref, kind: 'href' });
    }

    return patches;
  },

  // ---------------------------------------------------------------------------
  // business.json — name, phone (shows up across header, CTAs, footer)
  // ---------------------------------------------------------------------------
  business: (data, _ctx) => {
    const patches: ContentPatch[] = [];

    if (typeof data?.name === 'string')
      patches.push({ key: 'business.name', value: data.name });

    if (typeof data?.phone === 'string')
      patches.push({ key: 'business.phone', value: data.phone });

    // tel: href — use phoneRaw if available, otherwise phone
    const telNumber = data?.phoneRaw || data?.phone;
    if (typeof telNumber === 'string')
      patches.push({ key: 'business.phoneTel', value: `tel:${telNumber}`, kind: 'href' });

    // Address (shown in footer)
    const fullAddress = data?.address?.full || [
      data?.address?.street,
      data?.address?.city,
      data?.address?.state,
      data?.address?.zip,
    ].filter(Boolean).join(', ');
    if (fullAddress)
      patches.push({ key: 'business.address', value: fullAddress });

    return patches;
  },

  // ---------------------------------------------------------------------------
  // services.json — name + shortDescription for homepage cards (capped at 8)
  // ---------------------------------------------------------------------------
  services: (data, ctx) => {
    const patches: ContentPatch[] = [];
    const allServices: any[] = Array.isArray(data?.services) ? data.services : [];
    // Homepage ServiceGrid shows up to 8 services (bento shows featured + 8 rest)
    const capped = allServices.slice(0, 8);
    capped.forEach((service: any, i: number) => {
      if (typeof service?.name === 'string')
        patches.push({ key: `services.${i}.name`, value: substituteVars(service.name, ctx) });
      if (typeof service?.shortDescription === 'string')
        patches.push({ key: `services.${i}.shortDescription`, value: substituteVars(service.shortDescription, ctx) });
      if (typeof service?.image === 'string')
        patches.push({ key: `services.${i}.image`, value: service.image, kind: 'src' });
    });
    return patches;
  },

  posts: (data) => {
    const posts: any[] = Array.isArray(data?.posts) ? data.posts : [];
    return posts.slice(0, 3).flatMap((post, index) =>
      typeof post?.image === 'string'
        ? [{ key: `posts.${index}.image`, value: post.image, kind: 'src' as const }]
        : [],
    );
  },

  // ---------------------------------------------------------------------------
  // testimonials.json — quote (text) + author (name) for homepage slider
  //                     capped at 6 (grid variant shows 6, carousel shows 3/page)
  // ---------------------------------------------------------------------------
  testimonials: (data, ctx) => {
    const patches: ContentPatch[] = [];
    const allTestimonials: any[] = Array.isArray(data?.testimonials) ? data.testimonials : [];
    const capped = allTestimonials.slice(0, 6);
    capped.forEach((t: any, i: number) => {
      // template uses t.text for the quote body
      const quote = t?.text || t?.quote;
      if (typeof quote === 'string')
        patches.push({ key: `testimonials.${i}.quote`, value: substituteVars(quote, ctx) });
      if (typeof t?.name === 'string')
        patches.push({ key: `testimonials.${i}.author`, value: t.name });
    });
    return patches;
  },

  // ---------------------------------------------------------------------------
  // faqs.json — question + answer for homepage FAQ, capped at 8
  // ---------------------------------------------------------------------------
  faqs: (data, ctx) => {
    const patches: ContentPatch[] = [];
    // Mirror the homepage (app/page.tsx): it renders ONLY the `general`
    // category's FAQs (falling back to the first category), so index N here
    // must line up with the Nth rendered <FAQAccordion> item.
    let faqItems: any[] = [];
    if (Array.isArray(data?.faqs)) {
      faqItems = data.faqs;
    } else if (Array.isArray(data?.categories)) {
      const general = data.categories.find((c: any) => c?.slug === 'general');
      faqItems = (general?.faqs || data.categories[0]?.faqs || []) as any[];
    }
    faqItems.slice(0, 8).forEach((faq: any, i: number) => {
      if (typeof faq?.question === 'string')
        patches.push({ key: `faqs.${i}.question`, value: substituteVars(faq.question, ctx) });
      if (typeof faq?.answer === 'string')
        patches.push({ key: `faqs.${i}.answer`, value: substituteVars(faq.answer, ctx) });
    });
    return patches;
  },
};

export function derivePatches(
  file: DataFileName,
  data: any,
  ctx: BusinessContext
): ContentPatch[] {
  const fn = DERIVERS[file];
  return fn ? fn(data, ctx) : [];
}

/** Whether a given file can be previewed live (vs requiring Publish to see). */
export function hasLivePreview(file: DataFileName): boolean {
  return file === 'theme' || file in DERIVERS;
}
